// Who an Art Director may file a work report for.
//
// Kept out of the component so it can be driven directly by
// `scripts/verify-time-off-work-report.mjs`.
//
// Art Directors and Assistant Art Directors direct designers, and reporting on their behalf is the
// work this control now exists for. It is deliberately the narrowest version of that: never
// themselves — the control is for the people they direct, and self-reporting is the default actor
// anyway — never anybody who is not a designer, and never a designer they have no current team in
// common with.
//
// The shared-team test is the reason this is a rule and not a role check. Every participant on a
// Request is reachable from the popover, and "the designers on my teams" is the only reading of
// "the designers I direct" that the data can actually support. It fails closed: when either side's
// team list is empty, nothing is shared, and nothing is offered. A director who is offered nobody
// sees no control at all, which is the honest outcome — not a list of people they should not be
// filing for.
//
// Roles are plain strings so this module stays free of runtime imports and can be transpiled and
// driven on its own. They are the `UserRole` enum's own values, and the harness reads the shared
// package's enum to pin that they still are.

// The viewer's platform role, in the shared enum's values.
export const DESIGNER_USER_ROLE = 'core.designer';
export const ART_DIRECTOR_USER_ROLE = 'core.art_director';
export const ASSISTANT_ART_DIRECTOR_USER_ROLE = 'core.assistant_art_director';

// The roles this narrowed control applies to. Every role the Switch was already open to keeps the
// behaviour it had: this list is the set of viewers the filter is *for*, not a new allowlist.
export const DIRECTOR_WORK_REPORT_SWITCH_ROLES = [ART_DIRECTOR_USER_ROLE, ASSISTANT_ART_DIRECTOR_USER_ROLE];

export interface WorkReportSwitchViewer {
    userId: ID;
    role: string;
    // The viewer's own current teams
    teamIds: ID[];
}

export interface WorkReportSwitchCandidate {
    userId: ID;
    role: string;
    // The candidate's current teams on this task
    teamIds: ID[];
}

// Whether the viewer is one of the directors this filter applies to.
export const isDirectorWorkReportSwitch = (viewerRole: string): boolean =>
    DIRECTOR_WORK_REPORT_SWITCH_ROLES.includes(viewerRole);

// Whether a participant may be offered to this viewer as somebody to report for.
export const isWorkReportSwitchCandidateOffered = (
    viewer: WorkReportSwitchViewer,
    candidate: WorkReportSwitchCandidate
): boolean => {
    // Not a director: the control is exactly what it has always been for everybody it was already
    // open to. This change adds a viewer, it does not narrow an existing one.
    if (!isDirectorWorkReportSwitch(viewer.role)) {
        return true;
    }

    // A director does not report for themselves through a control meant for the people they direct
    if (candidate.userId === viewer.userId) {
        return false;
    }

    // Only designers. A project manager's or another director's week is not the director's to file
    if (candidate.role !== DESIGNER_USER_ROLE) {
        return false;
    }

    // And only the designers they actually direct: at least one current team in common. Empty on
    // either side shares nothing, so nothing is offered
    return candidate.teamIds.some((teamId) => viewer.teamIds.includes(teamId));
};
